import { createNotification } from '@/components';
import Link from '@/components/Link/Link';
import IntlMessages from '@/components/utility/intlMessages';
import { validateInvite } from '@/services/account/api';
import { Button, Checkbox, Flex, Form, Input, Modal, Space } from 'antd';
import { useEffect, useState } from "react";
import ReactPasswordChecklist from 'react-password-checklist';
import { useNavigate } from "react-router";
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';

export function RegisterForm() {
  const navigate = useNavigate();
  const intl = useIntl();
  const [form] = Form.useForm();
  const { Register } = useAuth();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isPasswordValid, setPasswordValid] = useState(false);
  const [inviteFieldsLoaded, setInviteFieldsLoaded] = useState(false);

  const values = Form.useWatch([], form);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, values]);

  const [searchParams] = useSearchParams();

  const invite = searchParams.get("invite");

  useEffect(() => {
    if (invite) {
      validateInvite(invite)
        .then((res) => {
          form.setFieldsValue({
            company: res.company,
            email: res.email
          });
          setInviteFieldsLoaded(true);
        })
        .catch(() => {
          createNotification(
            "error",
            intl.formatMessage({ id: "page.register.validate.invite" }),
          );
        });
    }
  }, [invite]);

  async function onSubmit(values) {
    setIsSubmitting(true);

    const payload = {
      ...values,
      name: `${values.first_name} ${values.last_name}`,
      invite: invite
    }

    await Register(payload)
      .then((signed) => {
        if (signed) {
          return navigate("/");
        }
          return navigate(`/confirm-email/${email}`);
      })
      .catch((err) => {
        if (err.response) {
          createNotification("error", err.response.data.message);
        } else {
          createNotification("error", "Network error");
        }
      })
      .finally(() => setIsSubmitting(false));
  }

  const showPasswordList = () => {
    if (password !== confirmPassword) {
      return true;
    }
    if (!password) {
      return false;
    }
    return !!(password && !isPasswordValid);
  };

  return (
    <>
      <Form
        layout={"vertical"}
        form={form}
        onFinish={(values) => {
          onSubmit(values);
        }}
      >
        <Flex gap="small" justify="space-between" horizontal className="w-full">
          <Form.Item
            name="first_name"
            label={<IntlMessages id="page.signUpFirstName" />}
            rules={[
              {
                required: true,
                message: <IntlMessages id="page.signUpFirstName.message" />,
              },
            ]}
            style={{
              display: 'inline-block',
              width: 'calc(50% - 8px)',
            }}
          >
            <Input
              size="large"
              placeholder={intl.formatMessage({ id: "page.signUpFirstName.placeholder" })}
            />
          </Form.Item>
          <Form.Item
            name="last_name"
            label={<IntlMessages id="page.signUpLastName" />}
            rules={[
              {
                required: true,
                message: <IntlMessages id="page.signUpLastName.message" />,
              },
            ]}
            style={{
              display: 'inline-block',
              width: 'calc(50% - 8px)',
            }}
          >
            <Input
              size="large"
              placeholder={intl.formatMessage({ id: "page.signUpLastName.placeholder" })}
            />
          </Form.Item>
        </Flex>
        <Form.Item
          name="company"
          label={<IntlMessages id="page.signUpCompany" />}
          rules={[
            {
              required: true,
              message: <IntlMessages id="page.signUpCompany.message" />,
            },
          ]}
        >
          <Input
            size="large"
            placeholder={intl.formatMessage({ id: "page.signUpCompany.placeholder" })}
            disabled={inviteFieldsLoaded}
          />
        </Form.Item>
        <Form.Item
          name="email"
          label={<IntlMessages id="page.signUpEmail" />}
          rules={[
            {
              required: true,
              message: <IntlMessages id="page.signUpEmail.message" />,
            },
            {
              type: "email",
              message: <IntlMessages id="page.signUpEmail.invalid" />,
            },
          ]}
        >
          <Input
            size="large"
            placeholder="saffe@saffe.com"
            disabled={inviteFieldsLoaded}
          />
        </Form.Item>
        <Form.Item
          name="password"
          label={<IntlMessages id="page.signUpPassword" />}
          rules={[
            {
              required: true,
              message: <IntlMessages id="page.signUpPassword.message" />,
            },
          ]}
        >
          <Input.Password
            success={isPasswordValid.toString()}
            placeholder='********'
            size="large"
            type="password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </Form.Item>
        {showPasswordList() && (
          <ReactPasswordChecklist
            rules={[
              "minLength",
              "specialChar",
              "number",
              "capital",
              "lowercase",
              "match",
            ]}
            messages={{
              minLength: intl.formatMessage({ id: "page.signUpPassword.minLength" }),
              specialChar: intl.formatMessage({ id: "page.signUpPassword.specialChar" }),
              number: intl.formatMessage({ id: "page.signUpPassword.number" }),
              capital: intl.formatMessage({ id: "page.signUpPassword.capital" }),
              lowercase: intl.formatMessage({ id: "page.signUpPassword.lowercase" }),
              match:  intl.formatMessage({ id: "page.signUpPassword.match" }),
            }}
            minLength={8}
            value={form.getFieldValue("password") || ""}
            valueAgain={form.getFieldValue("confirm_password") || ""}
            onChange={(isValid) => {
              setPasswordValid(isValid);
            }}
            iconSize={10}
            invalidColor="#E16408"
            validColor="#00ABAB"
          />
        )}
        <Form.Item
          name="confirm_password"
          label={<IntlMessages id="page.signUpConfirmPassword" />}
          rules={[
            {
              required: true,
              message: <IntlMessages id="page.signUpConfirmPassword.message" />,
            },
          ]}
        >
          <Input.Password
            size="large"
            type="password"
            placeholder='********'
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </Form.Item>
        <Form.Item
          name="agreement"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value ? Promise.resolve() : Promise.reject(new Error(intl.formatMessage({ id: 'page.signUpAgreement.message' }))),
            },
          ]}
        >
          <Checkbox>
            <IntlMessages id="page.signUpTermsConditions" />
            <Link
              onClick={() => setIsModalOpen(true)}
              className="!text-primary hover:text-primary cursor-pointer"
            >
              <IntlMessages id="page.signUpTermsConditionsCTA" />
            </Link>
          </Checkbox>
        </Form.Item>
        <Space align="baseline" className="flex justify-between">
          <div className="flex flex-col">
            <a className="text-primary hover:text-primary cursor-pointer" href="/login">
              <IntlMessages id="page.signUpAlreadyAccount" />
            </a>
          </div>
          <Form.Item>
            <Button
              className="bg-primary border-0 text-white !px-8 shadow-none"
              type="primary"
              shape="round"
              size="large"
              loading={isSubmitting}
              disabled={!submittable || !isPasswordValid}
              htmlType="submit"
            >
              <IntlMessages id="page.signUpButton" />
            </Button>
          </Form.Item>
        </Space>
      </Form>
      <Modal
        title="TERMS OF USE"
        open={isModalOpen}
        footer={null}
        width={860}
        bodyStyle={{ maxHeight: "823px", overflowY: "hidden" }}
        onCancel={() => setIsModalOpen(false)}
      >
        <iframe
          frameborder="0"
          height="600px"
          width="100%"
          src="https://docs.google.com/document/d/e/2PACX-1vSpDPxMKV3Y8BWidLsAqk0fqAfYitX2cirIPUDTgmrjcPsGR7XJCYoDCgIdnPogfowO7FDL-0ICg9IB/pub?embedded=true"
        />
      </Modal>
    </>
  )
}
